import React from 'react';
import RecordingProgress, { transcriptionStageLabel } from './RecordingProgress';
import { Button, Typography, Box, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { CREDIT_COSTS } from '../../constants/subscriptionPlans';
import MicIcon from '@mui/icons-material/Mic';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// Shared presentation only. The live container supplies its existing handlers;
// onboarding supplies example data and inert/no-op handlers. No data hooks here.
export default function ConsultationRecordingPanel({ recordedTranscriptionRecovery = null, readOnly = false, transcriptionStage, fileUploadProgress, handleRecordingFileUpload, handleStartTranscription, historyItems, isRecordingDragOver, isUploading, recordingSelectedItem, savingConsultation, setIsRecordingDragOver, setRecordingSelectedItem, setShowControlPanel, setTempTranscriptionLanguage, showControlPanel, tempTranscriptionLanguage, transcriptionLanguages }) {
  const busy = isUploading || savingConsultation || Boolean(transcriptionStage);
  return (<Box sx={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
                  <Box onClick={() => setShowControlPanel(!showControlPanel)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer', '&:hover': { backgroundColor: '#F9FAFB' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>Transcribe Recording</Typography>
                    {showControlPanel ? <KeyboardArrowDownIcon sx={{ color: '#6B7280' }} /> : <KeyboardArrowRightIcon sx={{ color: '#6B7280' }} />}
                  </Box>
                  {showControlPanel && (
                    <Box sx={{ px: 2, pb: 2, borderTop: '1px solid #F3F4F6', pt: 1.5 }}>

                      {/* Dropzone */}
                      <Box
                        data-tour="demo-recordings-file"
                        onDragOver={(e) => { e.preventDefault(); setIsRecordingDragOver(true); }}
                        onDragLeave={() => setIsRecordingDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsRecordingDragOver(false);
                          if (readOnly || busy) return;
                          const itemId = e.dataTransfer.getData('itemId');
                          if (itemId) {
                            const found = historyItems.find(i => i.id === itemId);
                            if (found) setRecordingSelectedItem(found);
                            return;
                          }
                          const files = e.dataTransfer.files;
                          if (files.length > 0) handleRecordingFileUpload(files[0]);
                        }}
                        sx={{
                          border: `2px dashed ${isRecordingDragOver ? '#29C0BB' : '#D1D5DB'}`,
                          borderRadius: '12px',
                          p: 2,
                          mb: 2,
                          textAlign: 'center',
                          backgroundColor: isRecordingDragOver ? '#F0FDFA' : '#F9FAFB',
                          transition: 'all 0.2s',
                        }}
                      >
                        {recordingSelectedItem ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <UploadFileIcon sx={{ fontSize: 20, color: '#29C0BB', flexShrink: 0 }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {recordingSelectedItem.title}
                              </Typography>
                            </Box>
                            <IconButton disabled={readOnly || busy} size="small" onClick={() => setRecordingSelectedItem(null)} sx={{ color: '#6B7280', flexShrink: 0 }}>
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <>
                            <UploadFileIcon sx={{ fontSize: 28, color: '#29C0BB', mb: 0.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                              Drag from list or{' '}
                              <Box
                                component="label"
                                sx={{ color: '#29C0BB', cursor: 'pointer', textDecoration: 'underline' }}
                              >
                                choose file
                                <input disabled={readOnly || busy}
                                  type="file"
                                  hidden
                                  accept=".wav,.mp3,.flac,.aac,.m4a,.ogg,.opus,.aiff,.webm,.mp4,.mpeg,.mov"
                                  onChange={(e) => { const f = e.target.files[0]; if (f) handleRecordingFileUpload(f); e.target.value = ''; }}
                                />
                              </Box>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 0.5 }}>
                              MP3, WAV, M4A, MP4, MOV and more
                            </Typography>
                          </>
                        )}
                      </Box>

                      {/* Language */}
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280', display: 'block', mb: 0.5 }}>Language</Typography>
                      <Select
                        isDisabled={readOnly || busy}
                        value={tempTranscriptionLanguage}
                        onChange={(val) => setTempTranscriptionLanguage(val)}
                        options={transcriptionLanguages}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          control: base => ({ ...base, border: '1px solid #D1D5DB', boxShadow: 'none', borderRadius: 6, fontSize: '0.75rem', minHeight: 34, '&:hover': { borderColor: '#29C0BB' } }),
                          container: base => ({ ...base, width: '100%', marginBottom: '12px' }),
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                      />

                      <RecordingProgress stage={transcriptionStage} isUploading={isUploading} progress={fileUploadProgress} />

                      {/* Start Transcription */}
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={readOnly || !recordingSelectedItem || busy || Boolean(recordedTranscriptionRecovery?.audioItem?.path && recordedTranscriptionRecovery.audioItem.path === recordingSelectedItem?.path)}
                        onClick={handleStartTranscription}
                        startIcon={savingConsultation ? <CircularProgress size={16} color="inherit" /> : isUploading ? <CircularProgress size={16} color="inherit" /> : <MicIcon />}
                        sx={{
                          py: 1.5,
                          backgroundColor: '#EF4444',
                          color: '#FFFFFF',
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          '&:hover': { backgroundColor: '#DC2626' },
                          '&.Mui-disabled': { backgroundColor: '#FCA5A5', color: '#FFFFFF' },
                        }}
                      >
                        {transcriptionStage ? transcriptionStageLabel(transcriptionStage) : savingConsultation ? 'Saving Consultation...' : isUploading ? 'Uploading...' : <>Start Transcription <Box component="span" sx={{ ml: 0.75, fontSize: '0.85em', fontWeight: 400 }}>({CREDIT_COSTS.recordedTranscriptionPerHour} credit / 1 hour)</Box></>}
                      </Button>

                    </Box>
                  )}
                </Box>);
}
